<template>
  <div class="form-outline">
    <label class="label" :class="{ disabled: isDisabled }">
      <span v-if="!!label">{{ label }}</span>
      <span v-else>{{ $t("tourClient.selectClient") }}*</span>
    </label>

    <v-select
      v-if="status === 'loaded'"
      v-model="localValue"
      :disabled="isDisabled"
      :clear-search-on-select="true"
      :clearable="clearable"
      :filterable="false"
      :options="clients"
      :reduce="!returnObject ? ((client: { id: any; }) => client.id) : undefined"
      class="select-search"
      label="name"
      @search="onSearch"
      @update:model-value="$emit('update:modelValue', localValue)"
    >
      <template v-slot:no-options>
        <div class="my-4">{{ $t("tourClient.writeSearchClients") }}</div>
      </template>

      <template v-slot:option="option">
        <div class="d-center">
          <span>{{ option?.name }} {{ option?.surname }}</span>
          <span v-if="option?.clientCode" class="text-muted ms-8 text-xs">{{ option?.clientCode }}</span>
        </div>
      </template>

      <template v-slot:selected-option="option">
        <div class="selected d-center py-8">
          <span>{{ getSelectedLabel(option) }}</span>
          <span v-if="option?.clientCode" class="text-muted ms-8 text-xs">{{ option?.clientCode }}</span>
        </div>
      </template>

      <template #open-indicator="{ attributes }">
        <span v-bind="attributes">
          <v-icon icon="search" size="sm" />
        </span>
      </template>

      <template v-if="canCreate" #list-footer>
        <li ref="load" class="create-client">
          <v-button
            class="btn-icon h-fit fw-normal shadow-none py-0 px-16"
            size="sm"
            variant="link"
            @click="openModal('v-client-create-modal')"
          >
            <v-icon icon="plus" size="xs" space="me-8" />
            {{ $t("clientTour.createClient") }}
          </v-button>
        </li>
      </template>
    </v-select>

    <v-client-create-modal
      v-if="modalToShow === 'v-client-create-modal'"
      :agency="agency"
      @client="setNewClient"
      @closed="closeModal"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import vSelect from "vue-select";
import api from "@/api";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import Client from "@/api/objects/Client";
import useModalUtils from "@/helpers/ModalUtils";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VClientCreateModal from "@/components/shared/VClientCreateModal.vue";

export default defineComponent({
  name: "VSelectClientSearch",
  components: {
    VClientCreateModal,
    VIcon,
    vSelect,
    VButton,
  },
  emits: ["userAttached", "clear", "update:modelValue"],
  props: {
    modelValue: {
      type: [String, Number, Object],
      required: false,
    },
    isDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: false,
      default: "",
    },
    clientName: {
      type: String,
      required: false,
    },
    clientId: {
      type: Number,
      required: false,
    },
    canCreate: {
      type: Boolean,
      required: false,
      default: true,
    },
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    agency: {
      type: String,
      required: true,
    },
    returnObject: {
      type: Boolean,
      required: false,
      default: false,
    },
    onlyAgencies: {
      type: Boolean,
      required: false,
      default: false,
    },
    exclude: {
      type: Number,
      required: false,
      default: null,
    },
  },
  setup() {
    return {
      ...useModalUtils(),
    };
  },
  data() {
    return {
      localValue: this.modelValue,
      clients: [] as Client[],
      status: "loading" as string,
    };
  },
  async created() {
    this.status = "loading";

    if (!!this.clientName) {
      await this.loadClients(null, this.clientName);
    }

    if (!!this.clientId) {
      await this.loadClients(null, "", true);
    }

    this.status = "loaded";
  },
  methods: {
    onSearch(search: string, loading: any) {
      if (search.length) {
        loading(true);
        this.loadClients(loading, search);
      }
    },
    async loadClients(loading?: any, search?: string, withDefault?: boolean) {
      if (!!loading) {
        loading(true);
      }

      try {
        if (!search) {
          search = "";
        }

        let id: string | number = "";

        if (withDefault && !!this.clientId) {
          id = this.clientId;
        }

        const filters: any = { name: search, id };

        if (this.onlyAgencies) {
          filters.type = 'agency';
        }

        const query = { filters };

        this.clients = (await api.client.all(this.agency, query)).filter((client: Client) => client.id !== this.exclude);

        if (withDefault && !!this.clientId && !!this.clients && this.clients.length > 0) {
          const clientFound = this.clients.find((client: Client) => client.id === this.clientId);

          if (!!clientFound) {
            this.localValue = this.returnObject ? clientFound : clientFound.id;
          }
        }
      } catch (e: any) {
        console.error(e);

        this.$toast.error(e.response.data.message);
      } finally {
        if (!!loading) {
          loading(false);
        }
      }
    },
    resolveValue(client: Client) {
      return this.returnObject ? client : client.id;
    },
    getSelectedLabel(option: any) {
      if (option?.id && typeof option.name === "string") {
        return `${option.name} ${option.surname || ""}`.trim();
      }

      const id = option?.id || (typeof option?.name === "number" ? option.name : option);
      const found = this.clients.find((client: Client) => client.id === id);

      if (found) {
        return `${found.name} ${found.surname || ""}`.trim();
      }

      return `${option?.name || ""} ${option?.surname || ""}`.trim() || option;
    },
    async setNewClient(client: Client) {
      await this.loadClients(null, client.name);

      const createdClient =
        this.clients.find((existing: Client) => existing.id === client.id) || client;

      if (!this.clients.some((existing: Client) => existing.id === createdClient.id)) {
        this.clients = [createdClient, ...this.clients];
      }

      const value = this.resolveValue(createdClient);
      this.localValue = value;
      this.$emit("update:modelValue", value);
    },
  },
});
</script>
